import { Fragment, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSetRecoilState } from "recoil";
import { HasRole } from "../../context/HasRole";
import {
  toastBgState,
  toastMessageState,
  toastShowState,
} from "../../utils/atoms/atoms";

interface IMutationButton {
  id: number;
  type: "edit" | "delete";
  modalId?: string;
  roles: string[];
  mutationMessage?: string;
  symbol?: string;
  color?: "primary" | "secondary" | "success" | "danger" | "warning";

  // EDIT props
  fetchFn?: (id: number) => Promise<any>;
  onEditSuccess?: (data: any) => void;

  // DELETE props
  deleteFn?: (id: number) => Promise<any>;
  invalidateKeys?: string[];
}

const MutationButton = ({
  id,
  type,
  modalId,
  roles,
  fetchFn,
  onEditSuccess,
  deleteFn,
  mutationMessage,
  color = "secondary",
  symbol,
  invalidateKeys = [],
}: IMutationButton) => {
  const queryClient = useQueryClient();

  const setToastShow = useSetRecoilState(toastShowState);
  const setToastBg = useSetRecoilState(toastBgState);
  const setToastMessage = useSetRecoilState(toastMessageState);

  const notify = (bg: string, msg: string) => {
    setToastBg(bg);
    setToastMessage(msg);
    setToastShow(true);
  };

  const editMutation = useMutation({
    mutationFn: fetchFn!,
    onSuccess(data) {
      onEditSuccess?.(data);
    },
    onError() {
      notify("danger", "Something went wrong");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFn!,
    onSuccess() {
      invalidateKeys.forEach((key) => queryClient.invalidateQueries([key]));
      notify("success", mutationMessage || "Entry deleted successfully");
    },
    onError(error: any) {
      notify("danger", error?.message || "Something went wrong");
    },
  });

  const handleClick = useCallback(() => {
    if (type === "edit" && fetchFn) {
      editMutation.mutate(id);
    } else if (type === "delete" && deleteFn && confirm("Are you sure?")) {
      deleteMutation.mutate(id);
    }
  }, [id, type]);

  return (
    <Fragment>
      <HasRole roles={roles}>
        <div
          className={`btn btn-outline-${color} inv-${type}`}
          data-bs-toggle={type === "edit" ? "modal" : ""}
          data-bs-target={type === "edit" ? modalId : ""}
          onClick={handleClick}
        >
          <div>
            {symbol ? (
              <i className={`bi bi-${symbol}`}></i>
            ) : (
              <i
                className={
                  type === "edit" ? "bi bi-pencil-square" : "bi bi-trash3-fill"
                }
              ></i>
            )}
          </div>
        </div>
      </HasRole>
    </Fragment>
  );
};

export default MutationButton;
